import {
  useCreateGlowUpProduct,
  useUpdateGlowUpProduct,
} from '@/api/glow-up-product/glow-up-product.hooks';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useZodForm,
} from '@/lib/form';
import {
  glowUpProductSchema,
  updateGlowUpProductSchema,
  UpdateGlowUpProductSchemaType,
} from '@/schemas/glow-up-product.schema';
import { GlowUpProduct } from '@repo/types/src/glow-up-product';
import { CirclePlus, Pencil } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

interface Props {
  product?: GlowUpProduct;
  categoryId: number;
}

export const AddGlowUpProduct = ({ product, categoryId }: Props) => {
  const isEdit = product !== undefined;
  const createMutation = useCreateGlowUpProduct();
  const updateMutation = useUpdateGlowUpProduct();
  const [open, setOpen] = useState(false);

  const form = useZodForm({
    schema: isEdit ? updateGlowUpProductSchema : glowUpProductSchema,
    defaultValues: {
      categoryId: product?.categoryId ?? categoryId,
      title: product?.title ?? '',
      description: product?.description ?? '',
      link: product?.link ?? '',
      ctaText: product?.ctaText ?? '',
      position: product?.position ?? 0,
      active: product?.active ?? false,
    },
  });

  const handleSubmit = (payload: UpdateGlowUpProductSchemaType) => {
    if (isEdit) {
      updateMutation.mutate(
        { id: product.id, payload },
        {
          onSuccess: () => {
            setOpen(false);
            toast.success('Produit modifié avec succès !', { duration: 3000 });
          },
          onError: () =>
            toast.error('Erreur lors de la modification', { duration: 3000 }),
        },
      );
    } else {
      if (!payload.file) {
        toast.error('Veuillez ajouter une image', { duration: 3000 });
        return;
      }
      createMutation.mutate(
        { ...payload, file: payload.file },
        {
          onSuccess: () => {
            setOpen(false);
            toast.success('Produit créé avec succès !', { duration: 3000 });
          },
          onError: () =>
            toast.error('Erreur lors de la création', { duration: 3000 }),
        },
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {isEdit ? (
          <Button variant="ghost" size="icon">
            <Pencil size={20} />
          </Button>
        ) : (
          <Button>
            <div className="flex flex-row gap-2 items-center">
              <CirclePlus size={24} />
              <span>Ajouter un produit</span>
            </div>
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <Form
          form={form}
          onSubmit={handleSubmit}
          className="w-full flex flex-col space-y-4"
        >
          <DialogHeader />
          <DialogTitle className="text-center">
            {isEdit ? 'Modifier' : 'Ajouter'} un produit Glow Up
          </DialogTitle>
          <DialogDescription className="text-center">
            {isEdit
              ? 'Modifier le produit Glow Up'
              : 'Ajouter un produit Glow Up à cette catégorie'}
          </DialogDescription>

          <FormField
            control={form.control}
            name="title"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Titre</FormLabel>
                <FormControl>
                  <Input type="text" placeholder="Ex: Sérum visage" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Description (optionnel)</FormLabel>
                <FormControl>
                  <Input
                    type="text"
                    placeholder="Ex: Idéal pour les peaux sensibles"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="link"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Lien boutique</FormLabel>
                <FormControl>
                  <Input type="text" placeholder="https://..." {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="ctaText"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Texte du bouton CTA</FormLabel>
                <FormControl>
                  <Input type="text" placeholder="Ex: Découvrir" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="position"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Position (optionnel)</FormLabel>
                <FormControl>
                  <Input type="number" min={0} placeholder="0" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="file"
            render={({ field: { value: _, onChange, ...fieldProps } }) => (
              <FormItem>
                <FormLabel>Image</FormLabel>
                {isEdit && (
                  <FormDescription>
                    Laisser vide pour conserver l'image actuelle
                  </FormDescription>
                )}
                <FormControl>
                  <Input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    {...fieldProps}
                    onChange={(e) => onChange(e.target.files?.[0])}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <DialogFooter className="flex gap-2">
            <DialogClose asChild>
              <Button variant="ghost">Annuler</Button>
            </DialogClose>
            <Button type="submit">{isEdit ? 'Modifier' : 'Ajouter'}</Button>
          </DialogFooter>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
